import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, MessageCircle, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../mock";
import Logo from "./Logo";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#f4f0e6]/95 backdrop-blur shadow-sm" : "bg-[#f4f0e6]"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-5 pb-2">
        <div className="flex items-start justify-between">
          {/* Left: Account + chat */}
          <div className="flex flex-col gap-4 pt-2 w-32">
            <button className="flex items-center gap-2 text-[#5a5a52] hover:text-[#4a7ba1] transition-colors">
              <User size={20} strokeWidth={1.5} />
              <span className="text-sm tracking-wide">Log In</span>
            </button>
            <button className="text-[#5a5a52] hover:text-[#4a7ba1] transition-colors w-fit">
              <MessageCircle size={26} strokeWidth={1.2} />
            </button>
          </div>

          {/* Center: Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo size={170} />
          </Link>

          {/* Right: lang selector + mobile toggle */}
          <div className="flex items-start justify-end w-32 pt-2">
            <button className="hidden md:flex items-center gap-2 bg-[#4a7ba1]/85 hover:bg-[#4a7ba1] text-white rounded-full px-4 py-1.5 transition-colors">
              <span className="text-base">🇬🇧</span>
              <ChevronDown size={16} />
            </button>
            <button
              className="md:hidden text-[#5a5a52]"
              onClick={() => setOpen(!open)}
              aria-label="menu"
            >
              {open ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center justify-center flex-wrap gap-x-8 gap-y-2 mt-4 pb-1">
          {NAV_LINKS.map((n) => {
            const active = location.pathname === n.href;
            return (
              <Link
                key={n.href}
                to={n.href}
                className={`text-[15px] tracking-wide transition-colors relative ${
                  active
                    ? "text-[#1e3a52] font-medium"
                    : "text-[#5a5a52] hover:text-[#4a7ba1]"
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
              >
                {n.label}
                {active && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#4a7ba1]" />
                )}
              </Link>
            );
          })}
          <Link
            to="/waitlist"
            className="flex items-center gap-1.5 bg-[#d4a857] hover:bg-[#b8893d] text-[#0f2c44] px-5 py-1.5 rounded-sm transition-colors font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
          >
            Reserve <span>&rsaquo;</span>
          </Link>
        </nav>

        {/* Mobile nav */}
        {open && (
          <nav className="md:hidden flex flex-col gap-3 mt-4 pb-4 border-t border-[#4a7ba1]/20 pt-4">
            {NAV_LINKS.map((n) => (
              <Link
                key={n.href}
                to={n.href}
                className="text-[#5a5a52] hover:text-[#4a7ba1] text-[17px]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/waitlist"
              className="bg-[#d4a857] text-[#0f2c44] px-5 py-2 rounded-sm w-fit font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Reserve &rsaquo;
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}
